// AttendanceSummaryReport.js

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

import { DataGrid } from "@mui/x-data-grid";

import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import DownloadIcon from "@mui/icons-material/Download";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";

const API = process.env.REACT_APP_API_URL || "http://localhost:3001/api";
const BASE_COLUMN_WIDTHS = [140, 180, 180, 90];
const ATTENDANCE_COLUMN_WIDTH = 120;
const getToday = () => {
  const d = new Date();

  return [
    d.getFullYear(),
    String(d.getMonth() + 1).padStart(2, "0"),
    String(d.getDate()).padStart(2, "0"),
  ].join("-");
};

const fieldSx = {
  minWidth: 170,
  "& .MuiInputBase-input": {
    fontSize: 14,
  },
  "& .MuiInputLabel-root": {
    fontSize: 14,
  },
};

const getUser = () => {
  try {
    return JSON.parse(sessionStorage.getItem("user") || "{}");
  } catch {
    return {};
  }
};

const numberValue = (value) =>
  value === null || value === undefined || value === "" ? null : Number(value);

const getYearLabel = (year) => {
  if (year.label) return year.label;

  const labels = ["I Year", "II Year", "III Year", "IV Year", "V Year", "VI Year"];

  return labels[Number(year.year_number) - 1] || `Year ${year.year_number}`;
};

export default function AttendanceSummaryReport() {
  const user = useMemo(() => getUser(), []);

  const clientId = user.client_id;
  const collegeId = user.college_id;

  const baseUrl = useMemo(
    () => `${API}/attendance-summary/${clientId}/${collegeId}`,
    [clientId, collegeId]
  );

  const [filters, setFilters] = useState({
    fromDate: getToday(),
    toDate: "",
    acyearId: "",
    programId: "",
    branchId: "",
    section: "",
  });

  const [academicYears, setAcademicYears] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [branches, setBranches] = useState([]);

  const [report, setReport] = useState({
    years: [],
    rows: [],
  });

  const [loading, setLoading] = useState(false);
  const [optionsLoading, setOptionsLoading] = useState(false);

  const [message, setMessage] = useState("");
  const [severity, setSeverity] = useState("info");

  // References used to synchronize grouped headers and DataGrid scrolling.
  const gridRootRef = useRef(null);
  const headerScrollRef = useRef(null);
  const syncingScrollRef = useRef(false);

  const notify = useCallback((text, type = "info") => {
    setMessage(text);
    setSeverity(type);
  }, []);

  const updateFilter = useCallback((name, value) => {
    setFilters((previous) => ({
      ...previous,
      [name]: value,
    }));
  }, []);

  const buildParams = useCallback(() => {
    const params = {
      fromDate: filters.fromDate,
    };

    if (filters.toDate) params.toDate = filters.toDate;
    if (filters.acyearId) params.acyearId = filters.acyearId;
    if (filters.programId) params.programId = filters.programId;
    if (filters.branchId) params.branchId = filters.branchId;

    if (filters.section.trim()) {
      params.section = filters.section.trim();
    }

    return params;
  }, [filters]);

  // ------------------------------------------------------------
  // Initial dropdown data
  // ------------------------------------------------------------
  useEffect(() => {
    if (!clientId || !collegeId) return undefined;

    let cancelled = false;

    const loadOptions = async () => {
      setOptionsLoading(true);

      try {
        const [yearResponse, programResponse, branchResponse] = await Promise.all([
          axios.get(`${baseUrl}/academic-years`),
          axios.get(`${baseUrl}/programs`),
          axios.get(`${baseUrl}/branches`),
        ]);

        if (cancelled) return;

        setAcademicYears(yearResponse.data.data || []);
        setPrograms(programResponse.data.data || []);
        setBranches(branchResponse.data.data || []);
      } catch (error) {
        if (!cancelled) {
          notify(error.response?.data?.message || "Unable to load report filters.", "error");
        }
      } finally {
        if (!cancelled) setOptionsLoading(false);
      }
    };

    loadOptions();

    return () => {
      cancelled = true;
    };
  }, [baseUrl, clientId, collegeId, notify]);

  // ------------------------------------------------------------
  // Reload programs when Academic Year changes
  // ------------------------------------------------------------
  useEffect(() => {
    if (!clientId || !collegeId) return undefined;

    let cancelled = false;

    axios
      .get(`${baseUrl}/programs`, {
        params: filters.acyearId ? { acyearId: filters.acyearId } : {},
      })
      .then(({ data }) => {
        if (!cancelled) {
          setPrograms(data.data || []);
        }
      })
      .catch((error) => {
        if (!cancelled) {
          notify(error.response?.data?.message || "Unable to load programs.", "error");
        }
      });

    return () => {
      cancelled = true;
    };
  }, [baseUrl, clientId, collegeId, filters.acyearId, notify]);

  // ------------------------------------------------------------
  // Reload branches when Program changes
  // ------------------------------------------------------------
  useEffect(() => {
    if (!clientId || !collegeId) return undefined;

    let cancelled = false;

    axios
      .get(`${baseUrl}/branches`, {
        params: filters.programId ? { programId: filters.programId } : {},
      })
      .then(({ data }) => {
        if (!cancelled) {
          setBranches(data.data || []);
        }
      })
      .catch((error) => {
        if (!cancelled) {
          notify(error.response?.data?.message || "Unable to load branches.", "error");
        }
      });

    return () => {
      cancelled = true;
    };
  }, [baseUrl, clientId, collegeId, filters.programId, notify]);

  // ------------------------------------------------------------
  // Search report
  // ------------------------------------------------------------
  const validateDates = useCallback(() => {
    if (!filters.fromDate) {
      notify("Please select From Date.", "warning");
      return false;
    }

    if (filters.toDate && filters.toDate < filters.fromDate) {
      notify("To Date cannot be earlier than From Date.", "warning");
      return false;
    }

    return true;
  }, [filters.fromDate, filters.toDate, notify]);

  const handleSearch = async () => {
    if (!validateDates()) return;

    setLoading(true);

    try {
      const { data } = await axios.get(`${baseUrl}/report`, {
        params: buildParams(),
      });

      setReport({
        years: data.years || [],
        rows: data.rows || [],
      });

      notify(`${(data.rows || []).length} report rows loaded.`, "success");
    } catch (error) {
      setReport({
        years: [],
        rows: [],
      });

      notify(error.response?.data?.message || "Unable to generate attendance report.", "error");
    } finally {
      setLoading(false);
    }
  };

  // ------------------------------------------------------------
  // Reset filters
  // ------------------------------------------------------------
  const handleReset = () => {
    setFilters({
      fromDate: getToday(),
      toDate: "",
      acyearId: "",
      programId: "",
      branchId: "",
      section: "",
    });

    setReport({
      years: [],
      rows: [],
    });

    setMessage("");
  };

  // ------------------------------------------------------------
  // CSV / PDF exports
  // ------------------------------------------------------------
  const handleDownload = async (type) => {
    if (!validateDates()) return;

    try {
      const response = await axios.get(`${baseUrl}/report.${type}`, {
        params: {
          ...buildParams(),
          _refresh: Date.now(),
        },
        responseType: "blob",
        headers: {
          "Cache-Control": "no-cache",
          Pragma: "no-cache",
        },
      });

      const blob = new Blob([response.data], {
        type: type === "pdf" ? "application/pdf" : "text/csv;charset=utf-8",
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");

      link.href = url;
      link.download = `attendance-summary.${type}`;

      document.body.appendChild(link);
      link.click();
      link.remove();

      // Delay revocation to allow the browser to start the download.
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    } catch {
      notify(`Unable to export ${type.toUpperCase()}.`, "error");
    }
  };

  // ------------------------------------------------------------
  // Dynamic attendance columns
  // ------------------------------------------------------------
  const dynamicColumns = useMemo(() => {
    const columns = [];

    for (const year of report.years) {
      const prefix = `year_${year.year_number}`;

      columns.push(
        {
          key: `${prefix}_ts`,
          label: "TS",
          yearLabel: getYearLabel(year),
          yearNumber: Number(year.year_number),
          type: "total",
        },
        {
          key: `${prefix}_fn_avg`,
          label: "FN Avg",
          yearLabel: getYearLabel(year),
          yearNumber: Number(year.year_number),
          type: "average",
        },
        {
          key: `${prefix}_an_avg`,
          label: "AN Avg",
          yearLabel: getYearLabel(year),
          yearNumber: Number(year.year_number),
          type: "average",
        }
      );
    }

    return columns;
  }, [report.years]);

  // ------------------------------------------------------------
  // DataGrid columns
  // ------------------------------------------------------------
  const columns = useMemo(() => {
    const fixedColumns = [
      {
        field: "academic_year",
        headerName: "Academic Year",
        width: BASE_COLUMN_WIDTHS[0],
        minWidth: BASE_COLUMN_WIDTHS[0],
        sortable: true,
      },
      {
        field: "program_name",
        headerName: "Program",
        width: BASE_COLUMN_WIDTHS[1],
        minWidth: BASE_COLUMN_WIDTHS[1],
        sortable: true,
      },
      {
        field: "branch_name",
        headerName: "Branch",
        width: BASE_COLUMN_WIDTHS[2],
        minWidth: BASE_COLUMN_WIDTHS[2],
        sortable: true,
      },
      {
        field: "section",
        headerName: "Section",
        width: BASE_COLUMN_WIDTHS[3],
        minWidth: BASE_COLUMN_WIDTHS[3],
        sortable: true,
        valueFormatter: (params) => params.value || "-",
      },
    ];

    const attendanceColumns = dynamicColumns.map((column) => ({
      field: column.key,
      headerName: `${column.yearLabel} ${column.label}`,
      width: ATTENDANCE_COLUMN_WIDTH,
      minWidth: ATTENDANCE_COLUMN_WIDTH,
      sortable: true,
      type: "number",
      align: "right",
      headerAlign: "center",
      valueFormatter: (params) => {
        const value = numberValue(params.value);

        if (value === null || Number.isNaN(value)) {
          return "-";
        }

        return column.type === "average" ? value.toFixed(2) : value;
      },
    }));

    return [...fixedColumns, ...attendanceColumns];
  }, [dynamicColumns]);

  // ------------------------------------------------------------
  // Build rows with unique IDs
  // ------------------------------------------------------------

  // ------------------------------------------------------------
  // Build DataGrid rows using the backend summary fields
  // ------------------------------------------------------------

  // ------------------------------------------------------------
  // Build DataGrid rows using backend summary fields
  // ------------------------------------------------------------
  const rows = useMemo(
    () =>
      report.rows.map((row, index) => {
        const gridRow = {
          ...row,
          id:
            row.id ??
            `${row.academic_year}-${row.program_id}-${row.branch_id}-${row.section ?? ""}-${
              row.year_number ?? ""
            }-${index}`,
        };

        const prefix = `year_${row.year_number}`;

        // An empty To Date means a single-date report.
        const isSingleDate = !filters.toDate || filters.fromDate === filters.toDate;

        gridRow[`${prefix}_ts`] = Number(row.ts) || 0;

        gridRow[`${prefix}_fn_avg`] = isSingleDate
          ? Number(row.fn_present_total) || 0
          : row.fn_average == null
          ? null
          : Number(row.fn_average);

        gridRow[`${prefix}_an_avg`] = isSingleDate
          ? Number(row.an_present_total) || 0
          : row.an_average == null
          ? null
          : Number(row.an_average);

        return gridRow;
      }),
    [report.rows, filters.fromDate, filters.toDate]
  );

  // ------------------------------------------------------------
  // Total row
  // TS = total students across groups.
  // FN/AN Avg = weighted overall average across groups.
  // ------------------------------------------------------------
  const totalRow = useMemo(() => {
    const total = {
      id: "TOTAL",
      academic_year: "",
      program_name: "TOTAL",
      branch_name: "",
      section: "",
    };

    const isSingleDate = !filters.toDate || filters.fromDate === filters.toDate;

    (report.years || []).forEach((year) => {
      const prefix = `year_${year.year_number}`;

      // Use the exact dynamic field names displayed by DataGrid.
      total[`${prefix}_ts`] = rows.reduce(
        (sum, row) => sum + (Number(row[`${prefix}_ts`]) || 0),
        0
      );

      const fnPresent = rows.reduce((sum, row) => sum + (Number(row.fn_present_total) || 0), 0);

      const anPresent = rows.reduce((sum, row) => sum + (Number(row.an_present_total) || 0), 0);

      if (isSingleDate) {
        total[`${prefix}_fn_avg`] = rows.reduce(
          (sum, row) => sum + (Number(row[`${prefix}_fn_avg`]) || 0),
          0
        );

        total[`${prefix}_an_avg`] = rows.reduce(
          (sum, row) => sum + (Number(row[`${prefix}_an_avg`]) || 0),
          0
        );
      } else {
        const fnDates = rows.reduce((sum, row) => sum + (Number(row.fn_attendance_dates) || 0), 0);

        const anDates = rows.reduce((sum, row) => sum + (Number(row.an_attendance_dates) || 0), 0);

        total[`${prefix}_fn_avg`] = fnDates > 0 ? fnPresent / fnDates : null;

        total[`${prefix}_an_avg`] = anDates > 0 ? anPresent / anDates : null;
      }
    });

    return total;
  }, [rows, report.years, filters.fromDate, filters.toDate]);

  // ------------------------------------------------------------
  // Grouped two-row header
  // First four columns span both header rows.
  // Each academic year spans TS, FN Avg and AN Avg.
  // ------------------------------------------------------------
  const headerColumns = useMemo(() => {
    const fixed = [
      {
        key: "academic_year",
        label: "Academic Year",
        width: BASE_COLUMN_WIDTHS[0],
      },
      {
        key: "program_name",
        label: "Program",
        width: BASE_COLUMN_WIDTHS[1],
      },
      {
        key: "branch_name",
        label: "Branch",
        width: BASE_COLUMN_WIDTHS[2],
      },
      {
        key: "section",
        label: "Section",
        width: BASE_COLUMN_WIDTHS[3],
      },
    ];

    const years = report.years.map((year) => ({
      key: `year_${year.year_number}`,
      label: getYearLabel(year),
      width: ATTENDANCE_COLUMN_WIDTH * 3,
      subColumns: [
        {
          key: `year_${year.year_number}_ts`,
          label: "TS",
        },
        {
          key: `year_${year.year_number}_fn_avg`,
          label: "FN Avg",
        },
        {
          key: `year_${year.year_number}_an_avg`,
          label: "AN Avg",
        },
      ],
    }));

    return { fixed, years };
  }, [report.years]);

  const totalGridWidth = useMemo(() => {
    const fixedWidth = BASE_COLUMN_WIDTHS.reduce((total, width) => total + width, 0);

    return fixedWidth + report.years.length * ATTENDANCE_COLUMN_WIDTH * 3;
  }, [report.years.length]);

  // ------------------------------------------------------------
  // Keep grouped header horizontally aligned with DataGrid body.
  // DataGrid's own headers are hidden below.
  // ------------------------------------------------------------
  useEffect(() => {
    const root = gridRootRef.current;
    const header = headerScrollRef.current;

    if (!root || !header) return undefined;

    let virtualScroller = null;
    let observer = null;

    const syncHeaderFromGrid = () => {
      if (!virtualScroller || syncingScrollRef.current) return;

      syncingScrollRef.current = true;
      header.scrollLeft = virtualScroller.scrollLeft;
      syncingScrollRef.current = false;
    };

    const syncGridFromHeader = () => {
      if (!virtualScroller || syncingScrollRef.current) return;

      syncingScrollRef.current = true;
      virtualScroller.scrollLeft = header.scrollLeft;
      syncingScrollRef.current = false;
    };

    const attachScroller = () => {
      const nextScroller = root.querySelector(".MuiDataGrid-virtualScroller");

      if (!nextScroller || nextScroller === virtualScroller) return;

      if (virtualScroller) {
        virtualScroller.removeEventListener("scroll", syncHeaderFromGrid);
      }

      virtualScroller = nextScroller;

      virtualScroller.addEventListener("scroll", syncHeaderFromGrid, {
        passive: true,
      });

      header.scrollLeft = virtualScroller.scrollLeft;
    };

    attachScroller();

    observer = new MutationObserver(attachScroller);

    observer.observe(root, {
      childList: true,
      subtree: true,
    });

    header.addEventListener("scroll", syncGridFromHeader, {
      passive: true,
    });

    return () => {
      observer?.disconnect();

      virtualScroller?.removeEventListener("scroll", syncHeaderFromGrid);

      header.removeEventListener("scroll", syncGridFromHeader);
    };
  }, []);

  if (!clientId || !collegeId) {
    return (
      <Alert severity="error">Client ID or College ID is missing from sessionStorage.user.</Alert>
    );
  }

  return (
    <Card
      sx={{
        m: 2,
        borderRadius: 2,
        overflow: "hidden",
      }}
    >
      <Typography variant="h5" fontWeight={700}>
        ATTENDANCE SUMMARY REPORT
      </Typography>

      <Typography variant="body2">
        Academic-year, admitted-batch and year-wise attendance
      </Typography>

      <CardContent>
        {/* Filters */}
        <Box
          sx={{
            display: "flex",
            gap: 2,
            flexWrap: "wrap",
            alignItems: "center",
            mb: 2,
          }}
        >
          <TextField
            label="From Date"
            type="date"
            required
            value={filters.fromDate}
            onChange={(e) => updateFilter("fromDate", e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={fieldSx}
          />

          <TextField
            label="To Date (Optional)"
            type="date"
            value={filters.toDate}
            onChange={(e) => updateFilter("toDate", e.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{
              min: filters.fromDate || undefined,
            }}
            sx={fieldSx}
          />

          <TextField
            select
            label="Academic Year (Optional)"
            value={filters.acyearId}
            onChange={(e) => {
              const value = e.target.value;

              setFilters((previous) => ({
                ...previous,
                acyearId: value,
                programId: "",
                branchId: "",
              }));
            }}
            SelectProps={{ native: true }}
            InputLabelProps={{ shrink: true }}
            sx={fieldSx}
          >
            <option value="">All Configured Academic Years</option>

            {academicYears.map((item) => (
              <option key={item.acyear_id} value={item.acyear_id}>
                {item.academic_year}
              </option>
            ))}
          </TextField>

          <TextField
            select
            label="Program (Optional)"
            value={filters.programId}
            onChange={(e) => {
              const value = e.target.value;

              setFilters((previous) => ({
                ...previous,
                programId: value,
                branchId: "",
              }));
            }}
            SelectProps={{ native: true }}
            InputLabelProps={{ shrink: true }}
            sx={fieldSx}
          >
            <option value="">All Programs</option>

            {programs.map((item) => (
              <option key={item.program_id} value={item.program_id}>
                {item.program_name || `Program ${item.program_id}`}
              </option>
            ))}
          </TextField>

          <TextField
            select
            label="Branch (Optional)"
            value={filters.branchId}
            onChange={(e) => updateFilter("branchId", e.target.value)}
            SelectProps={{ native: true }}
            InputLabelProps={{ shrink: true }}
            sx={fieldSx}
          >
            <option value="">All Branches</option>

            {branches.map((item) => (
              <option key={item.branch_id} value={item.branch_id}>
                {item.branch_name || `Branch ${item.branch_id}`}
              </option>
            ))}
          </TextField>

          <TextField
            label="Section (Optional)"
            value={filters.section}
            onChange={(e) => updateFilter("section", e.target.value)}
            sx={fieldSx}
          />

          <Button
            variant="contained"
            startIcon={<SearchIcon />}
            onClick={handleSearch}
            disabled={loading || optionsLoading}
            sx={{
              backgroundColor: "#1976d2",
              color: "#ffffff",
              fontWeight: 700,
              "&:hover": {
                backgroundColor: "#115293",
              },
              "&.Mui-disabled": {
                backgroundColor: "#bbdefb",
                color: "#ffffff",
              },
            }}
          >
            Search
          </Button>

          <Button
            variant="contained"
            startIcon={<RefreshIcon />}
            onClick={handleReset}
            disabled={loading}
            sx={{
              backgroundColor: "#616161",
              color: "#ffffff",
              fontWeight: 700,
              "&:hover": {
                backgroundColor: "#424242",
              },
            }}
          >
            Reset
          </Button>
        </Box>

        {optionsLoading && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Loading filter options...
          </Typography>
        )}

        {/* Export buttons */}
        <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap">
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={() => handleDownload("csv")}
            disabled={loading}
            sx={{
              backgroundColor: "#2e7d32",
              color: "#ffffff",
              fontWeight: 700,
              "&:hover": {
                backgroundColor: "#1b5e20",
              },
            }}
          >
            Export CSV
          </Button>

          <Button
            variant="contained"
            startIcon={<PictureAsPdfIcon />}
            onClick={() => handleDownload("pdf")}
            disabled={loading}
            sx={{
              backgroundColor: "#d32f2f",
              color: "#ffffff",
              fontWeight: 700,
              "&:hover": {
                backgroundColor: "#b71c1c",
              },
            }}
          >
            Export PDF
          </Button>
        </Stack>

        {/* Two-level grouped header */}
        <Box
          sx={{
            width: "100%",
            overflow: "hidden",
            border: "1px solid #c5d7eb",
            borderBottom: 0,
          }}
        >
          <Box
            ref={headerScrollRef}
            sx={{
              width: "100%",
              overflowX: "auto",
              overflowY: "hidden",
              scrollbarWidth: "none",
              "&::-webkit-scrollbar": { display: "none" },
            }}
          >
            <Box
              sx={{
                width: totalGridWidth,
                minWidth: totalGridWidth,
              }}
            >
              {/* FIRST HEADER ROW */}
              <Box
                sx={{
                  display: "flex",
                  height: 40,
                }}
              >
                {headerColumns.fixed.map((column) => (
                  <Box
                    key={column.key}
                    sx={{
                      flex: `0 0 ${column.width}px`,
                      width: column.width,
                      minWidth: column.width,
                      boxSizing: "border-box",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      px: 1,
                      fontSize: 13,
                      fontWeight: 700,
                      color: "#fff",
                      backgroundColor: "#1565c0",
                      borderRight: "1px solid #fff",
                      borderBottom: "1px solid #fff",
                    }}
                  >
                    {column.label}
                  </Box>
                ))}

                {headerColumns.years.map((year) => (
                  <Box
                    key={year.key}
                    sx={{
                      flex: `0 0 ${year.width}px`,
                      width: year.width,
                      minWidth: year.width,
                      boxSizing: "border-box",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 14,
                      fontWeight: 700,
                      color: "#fff",
                      backgroundColor: "#1565c0",
                      borderRight: "1px solid #fff",
                      borderBottom: "1px solid #fff",
                    }}
                  >
                    {year.label}
                  </Box>
                ))}
              </Box>

              {/* SECOND HEADER ROW */}
              <Box
                sx={{
                  display: "flex",
                  height: 38,
                }}
              >
                {/* Empty cells directly under Academic Year, Program,
            Branch and Section */}
                {headerColumns.fixed.map((column) => (
                  <Box
                    key={`empty-${column.key}`}
                    sx={{
                      flex: `0 0 ${column.width}px`,
                      width: column.width,
                      minWidth: column.width,
                      boxSizing: "border-box",
                      backgroundColor: "#e3f2fd",
                      borderRight: "1px solid #c5d7eb",
                      borderBottom: "1px solid #c5d7eb",
                    }}
                  />
                ))}

                {headerColumns.years.flatMap((year) =>
                  year.subColumns.map((column) => (
                    <Box
                      key={column.key}
                      sx={{
                        flex: `0 0 ${ATTENDANCE_COLUMN_WIDTH}px`,
                        width: ATTENDANCE_COLUMN_WIDTH,
                        minWidth: ATTENDANCE_COLUMN_WIDTH,
                        boxSizing: "border-box",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: 12,
                        fontWeight: 700,
                        color: "#0d47a1",
                        backgroundColor: "#e3f2fd",
                        borderRight: "1px solid #c5d7eb",
                        borderBottom: "1px solid #c5d7eb",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {column.label}
                    </Box>
                  ))
                )}
              </Box>
            </Box>
          </Box>
        </Box>

        {/* -------------------------------------------------- */}
        {/* DataGrid body. Built-in headers are hidden because */}
        {/* the grouped two-row header is rendered above.      */}
        {/* -------------------------------------------------- */}
        <Box
          ref={gridRootRef}
          sx={{
            width: "100%",
            "& .MuiDataGrid-root": {
              border: "1px solid #c5d7eb",
              borderTop: 0,
              borderRadius: "0 0 4px 4px",
              fontSize: 13,
            },
            "& .MuiDataGrid-columnHeaders": {
              display: "none",
              height: "0 !important",
              minHeight: "0 !important",
              maxHeight: "0 !important",
              border: 0,
            },
            "& .MuiDataGrid-virtualScroller": {
              marginTop: "0 !important",
            },
            "& .MuiDataGrid-cell": {
              borderRight: "1px solid #e0e0e0",
              borderBottom: "1px solid #e0e0e0",
              fontSize: 13,
            },
            "& .MuiDataGrid-row:nth-of-type(even)": {
              backgroundColor: "#f5f9ff",
            },
            "& .MuiDataGrid-row:hover": {
              backgroundColor: "#e3f2fd",
            },
            "& .MuiDataGrid-footerContainer": {
              backgroundColor: "#f5f9ff",
              borderTop: "1px solid #c5d7eb",
            },
          }}
        >
          {loading && report.rows.length === 0 ? (
            <Box
              sx={{
                height: 300,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                border: "1px solid #c5d7eb",
              }}
            >
              <CircularProgress />
            </Box>
          ) : (
            <DataGrid
              rows={rows.length > 0 ? [...rows, totalRow] : rows}
              columns={columns}
              getRowId={(row) => row.id}
              getRowClassName={(params) => (params.row.id === "TOTAL" ? "total-row" : "")}
              loading={loading}
              autoHeight
              pageSize={20}
              rowsPerPageOptions={[10, 25, 50, 100]}
              disableSelectionOnClick
              disableColumnMenu
              disableColumnFilter
              disableColumnSelector
              disableDensitySelector
              density="compact"
              localeText={{
                noRowsLabel: "No report data. Select From Date and click Search.",
              }}
            />
          )}
        </Box>
      </CardContent>

      {/* Notifications */}
      <Snackbar
        open={Boolean(message)}
        autoHideDuration={4000}
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        onClose={() => setMessage("")}
      >
        <Alert severity={severity} variant="filled" onClose={() => setMessage("")}>
          {message}
        </Alert>
      </Snackbar>
    </Card>
  );
}
